import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  PostAdd as PostAddIcon,
  OpenInNew as OpenInNewIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid, PageTitle, GridEditButton } from '../../../components/common';
import { useGuiaProduccionStore } from './store/useGuiaProduccionStore';
import type { EstadoGuiaProduccion } from './types';

const estadoLabels: Record<EstadoGuiaProduccion, string> = {
  'PEN': 'Pendiente',
  'APR': 'Aprobado',
  'ANU': 'Anulado',
};

const estadoColors: Record<EstadoGuiaProduccion, 'warning' | 'success' | 'error'> = {
  'PEN': 'warning',
  'APR': 'success',
  'ANU': 'error',
};

const GuiasProduccionPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    guias,
    loading,
    error,
    fetchGuias,
    clearError
  } = useGuiaProduccionStore();

  // Para mostrar solo un botón "Generar Parte" por DERC en el listado
  const getFirstGuiaIdByDerc = (): Set<number> => {
    const dercProcessed = new Set<string>();
    const firstGuiaIds = new Set<number>();

    // Ordenar por ID para consistencia (mostrar botón en la primera guía)
    const sortedGuias = [...guias].sort((a, b) => a.id - b.id);

    for (const guia of sortedGuias) {
      const isAprobado = guia.estado === 'APR';
      const sinParte = !guia.tieneParte;
      const derc = guia.numeroDocumento;

      if (isAprobado && sinParte && derc && !dercProcessed.has(derc)) {
        dercProcessed.add(derc);
        firstGuiaIds.add(guia.id);
      }
    }

    return firstGuiaIds;
  };

  const firstGuiaIdsForParte = getFirstGuiaIdByDerc();

  const handleNavigateToGuiaMateriaPrima = (idGuiaMateriaPrima: number) => {
    navigate(`/produccion/guias-materia-prima/${idGuiaMateriaPrima}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'N° Guía',
      width: 70,
      type: 'number'
    },
    {
      field: 'numeroGuiaMateriaPrima',
      headerName: 'Guía MP',
      width: 90,
      renderCell: (params: GridRenderCellParams) => {
        const idGuiaMateriaPrima = params.row.idGuiaMateriaPrima;
        const numeroGuia = params.value;

        if (!idGuiaMateriaPrima) return '-';

        return (
          <Tooltip title="Ver Guía de Materia Prima">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                color: 'primary.main',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleNavigateToGuiaMateriaPrima(idGuiaMateriaPrima);
              }}
            >
              {numeroGuia || idGuiaMateriaPrima}
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'idOrigenProducto',
      headerName: 'Tipo',
      width: 50,
      renderCell: (params: GridRenderCellParams) => {
        const tipo = params.value === 1 ? 'A' : params.value === 2 ? 'B' : '-';
        return (
          <Chip
            label={tipo}
            size="small"
            color={params.value === 1 ? 'primary' : 'error'}
            variant="filled"
          />
        );
      },
    },
    {
      field: 'numeroDocumento',
      headerName: 'N° DERC',
      width: 130,
    },
    {
      field: 'fechaExtraccion',
      headerName: 'Fecha',
      width: 90,
      type: 'date',
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('es-ES');
      }
    },
    {
      field: 'proveedor',
      headerName: 'Proveedor',
      width: 120,
      flex: 1,
      minWidth: 100
    },
    {
      field: 'asociacion',
      headerName: 'Asociación',
      width: 100,
      renderCell: (params: GridRenderCellParams) => {
        const isTypeB = params.row.idOrigenProducto === 2;
        if (isTypeB) return 'N/A';
        return params.value || '';
      },
    },
    {
      field: 'duenio',
      headerName: 'Dueño',
      width: 110
    },
    {
      field: 'encargado',
      headerName: 'Encargado',
      width: 110
    },
    {
      field: 'numeroMallas',
      headerName: 'Mallas',
      width: 60,
      type: 'number'
    },
    {
      field: 'subLoteCantidad',
      headerName: 'Sub Lote',
      width: 75,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        // Only show Sub Lote for Type A (idOrigenProducto === 1)
        const isTypeA = params.row.idOrigenProducto === 1;
        if (!isTypeA || params.value == null) return '';
        return (
          <Chip
            label={params.value}
            size="small"
            color="primary"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const estado = (params.value as EstadoGuiaProduccion) || 'PEN';
        const tieneParte = params.row.tieneParte;

        // Si tiene parte, mostrar "Con Parte" en lugar del estado normal
        if (tieneParte) {
          return (
            <Chip
              label="Con Parte"
              color="info"
              size="small"
            />
          );
        }

        return (
          <Chip
            label={estadoLabels[estado]}
            color={estadoColors[estado]}
            size="small"
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isPendiente = params.row.estado === 'PEN';
        const tieneParte = params.row.tieneParte;
        // Solo mostrar botón "Generar Parte" en la primera guía de cada DERC
        const showGenerarParte = firstGuiaIdsForParte.has(params.row.id);
        // Permitir editar si no tiene parte (puede cambiar estado)
        const canEdit = !tieneParte;

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {canEdit && (
              <GridEditButton
                onClick={() => handleEdit(params.row.id)}
                tooltip={isPendiente ? "Editar" : "Cambiar estado"}
                fontSize="small"
                ariaLabel={`Editar guía ${params.row.id}`}
              />
            )}
            {tieneParte && (
              <>
                <Tooltip title="Ver Guía">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleView(params.row.id)}
                    aria-label={`Ver guía ${params.row.id}`}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Ver Parte de Producción">
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => handleViewParte(params.row.idParte)}
                    aria-label={`Ver parte de producción de guía ${params.row.id}`}
                  >
                    <PostAddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {showGenerarParte && (
              <Tooltip title="Generar Parte de Producción">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleGenerarParte(params.row.id)}
                  aria-label={`Generar parte para guía ${params.row.id}`}
                >
                  <PostAddIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    fetchGuias();
  }, [fetchGuias]);

  const _handleAdd = () => {
    navigate('/produccion/guias-produccion/new');
  };
  void _handleAdd; // Explicitly mark as intentionally unused

  const handleEdit = (id: number) => {
    navigate(`/produccion/guias-produccion/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/produccion/guias-produccion/${id}?readonly=true`);
  };

  const handleViewParte = (idParte?: number) => {
    if (idParte) {
      navigate(`/produccion/partes-produccion/${idParte}?readonly=true`);
    }
  };

  const handleGenerarParte = (id: number) => {
    navigate(`/produccion/partes-produccion/new?guiaId=${id}`);
  };

  const handleCloseError = () => {
    clearError();
  };

  const handleGoToGuiasMateriaPrima = () => {
    navigate('/produccion/guias-materia-prima');
  };

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      <PageTitle
        title="Guías de Producción"
        subtitle="Gestión de Guías de Producción"
        flowStep={2}
        actions={
          <Button
            variant="outlined"
            color="primary"
            startIcon={<InventoryIcon />}
            onClick={handleGoToGuiasMateriaPrima}
            size="small"
          >
            Guías Materia Prima
          </Button>
        }
      />

      <Box sx={{ width: '100%' }}>

        {error && (
          <Alert
            severity="error"
            onClose={handleCloseError}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={guias}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            pageSize={25}
          />
        )}
      </Box>
    </Box>
  );
};

export default GuiasProduccionPage;